import { useEffect } from "react"
import { CancelButton, CommandButton, Loading } from "components"
import { ActionButton } from "../components"
import { InputSection, ModalAttachmentPaymentBillService } from "./components"
import { AccountProvider } from "contexts"
import { BillContext } from "./contexts"
import { yupResolver } from "@hookform/resolvers/yup"
import { FormProvider, useForm } from "react-hook-form"
import toast from "react-hot-toast"
import { useNavigate, useParams } from "react-router-dom"
import { APPROVAL, useApi } from "utils"
import { useApprovalStore } from "../utils"
import { PaymentBillServiceSchema, paymentBillServiceSchema } from "./schema"
import { useDetailPaymentBillService } from "./hooks"
import moment from "moment"

type UpdatePaymentBillServiceProps = {
  disable?: boolean
  bill_id?: number | null
}

export const UpdatePaymentBillService = ({ bill_id, disable }: UpdatePaymentBillServiceProps) => {
  const { id } = useParams()
  const api = useApi()
  const navigate = useNavigate()
  const detailBill = useDetailPaymentBillService(disable ? bill_id?.toString()! : id!)
  const { setApproval, resetApproval } = useApprovalStore()

  const defaultValues: PaymentBillServiceSchema = {
    _balance: 0,
    coa_id: "",
    memo: "",
    deposit_amount: 0,
    payment_amount: 0,
    referensi: "",
    trx_code: "",
    transaction_date: moment().format("YYYY-MM-DD"),
    vendor_id: "",
    imgPath: [],
  }
  const methods = useForm<PaymentBillServiceSchema>({ defaultValues, resolver: yupResolver(paymentBillServiceSchema) })
  const onSubmit = (value: PaymentBillServiceSchema) => {
    const final_value = {
      bill_id: detailBill?.data?.bill_id,
      trx_code: value.trx_code,
      vendor_id: String(detailBill?.data?.vendor_id),
      location_id: String(detailBill?.data?.location_id),
      address: detailBill?.data?.address,
      memo: value.memo,
      transaction_date: value.transaction_date ?? moment().format("YYYY-MM-DD"),
      referensi: value.referensi,
      coa_id: value.coa_id,
      deposit_amount: value.deposit_amount,
      payment_amount: value.payment_amount,
      imgPath: value.imgPath || [],
      payment_bill_id: detailBill?.data?.payment_bill_id
    }

    return new Promise<void>((resolve) => {
      const submitForm = (): void => {
        toast.promise(api.put("/paymentbill/updatepayment", final_value), {
          loading: "Loading...",
          success: (res) => res.data.message,
          error: (err) => err.response.data.detail?.message ?? err.response.data.message,
        })
        .then(() => navigate(-1))
        .catch(() => {})
        .finally(resolve)
      }

      if (!value.payment_amount || value.payment_amount <= 0) {
        toast.error("Payment Amount must be greater than 0")
        return resolve() 
      } else if(value.payment_amount > value._balance) {
        toast.error("Payment Amount cannot be more than Balance Bill Service")
        return resolve() 
      }

      submitForm()
    })
  }

  useEffect(() => {
    if (detailBill?.data) {
      const newValues: PaymentBillServiceSchema = {
        _balance: 0,
        coa_id: String(detailBill?.data?.coa_id)!,
        memo: detailBill?.data?.memo!,
        deposit_amount: detailBill?.data?.deposit_amount!,
        payment_amount: detailBill?.data?.payment_amount!,
        referensi: detailBill?.data?.referensi!,
        trx_code: detailBill?.data?.trx_code!,
        transaction_date: detailBill?.data?.transaction_date ?? moment().format("YYYY-MM-DD"),
        vendor_id: String(detailBill?.data?.vendor_id)!,
        imgPath: detailBill?.data?.attachment,
      };

      methods.reset(newValues);
    }

    // eslint-disable-next-line
  }, [detailBill?.data]);

  useEffect(() => {
    resetApproval()
    setApproval(detailBill?.data?.approve_status || 0)
    return () => resetApproval()

  }, [detailBill?.data, setApproval, resetApproval])

  return (
    <div>{detailBill?.isLoading || !detailBill?.data ? (<Loading loading={detailBill?.isLoading} />) : (
      <BillContext.Provider
        value={{
          approve_by: Number(detailBill?.data?.approve_by) ?? null,
          approve_status: Number(detailBill?.data?.approve_status) ?? 0,
          bill_id: Number(detailBill?.data?.bill_id) ?? 0,
          coa_id: "",
          deposit_amount: 0,
          memo: detailBill?.data?.memo ?? "",
          payment_amount: 0,
          purchase_order_id: 0,
          referensi: detailBill?.data?.referensi ?? "",
          transaction_date: detailBill?.data?.transaction_date ?? moment().format("YYYY-MM-DD"),
          trx_code: detailBill?.data?.trx_code ?? "",
          databill: {
            address: detailBill?.data?.address ?? "",
            balance: detailBill?.data?.balance_bill ?? 0,
            location_id: String(detailBill?.data?.location_id) ?? "",
            saldo_deposit: 0,
            trx_code: detailBill?.data?.billcode ?? "",
            vendor_id: String(detailBill?.data?.vendor_id) ?? "",
            vendor_name: detailBill?.data?.vendor_name ?? ""
          }
        }}
      >
        <AccountProvider>
          <FormProvider {...methods}>
            <InputSection
              disable={disable}
              buttonsection={disable ? <div /> : 
                <ActionButton>
                  <section className="flex flex-wrap gap-3">
                    {/* {detailBill?.data?.approve_status === APPROVAL.APPROVED ? (<PrintPaymentBillServiceUpdate />) : (<CommandButton actiontype="print" />)} */}
                    <ModalAttachmentPaymentBillService code={methods?.control?._formValues?.trx_code!} />
                  </section>

                  <section className="flex flex-wrap gap-3">
                    <CancelButton />

                    {/* {detailBill?.data?.approve_status === APPROVAL.PENDING && <div>
                      <CommandButton actiontype="delete" onClick={toggle} />

                      {isActive && (
                        <ModalDeletePaymentBillService
                          payment_bill_id={String(detailBill?.data?.payment_bill_id)!}
                          toggle={toggle}
                          onSuccess={() => navigate(-1)}
                        />
                      )}
                    </div>} */}

                    {detailBill?.data?.approve_status !== APPROVAL.APPROVED && (
                      <CommandButton
                        actiontype="save"
                        loading={methods.formState.isSubmitting ? "true" : undefined}
                        onClick={methods.handleSubmit(onSubmit)}
                      />
                    )}
                  </section>
                </ActionButton>
              }
            />
          </FormProvider>
        </AccountProvider>
      </BillContext.Provider>
    )}</div>
  )
}